import { Component, inject, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject, take, tap, takeUntil, distinctUntilChanged, startWith, combineLatest, filter } from 'rxjs';

import { ReportService } from '../services/report.service';
import { IReportCard, IReportCardDistrictCount, IReportCardGraph, IReportCardInactiveCount, IReportCardSchoolCount } from '../interfaces/ireport-card';
import { AuthService } from '@core/services/auth.service';
import { LookupService } from '@core/services/lookup.service';
import { ReportCourseViewComponent } from '../report-course-view/report-course-view.component';
import { Role } from '@core/enums/roles';
import { GraphType } from '../enums/report-graph';
import { IDistrict } from '@core/interfaces/ilookup';
import { ISchoolYear } from '@core/interfaces/ischool-year';
import { IReportCardRequest } from '../interfaces/ireport-request';

@Component({
  selector: 'prism-report-cards',
  templateUrl: './report-cards.component.html',
  styleUrl: './report-cards.component.scss'
})
export class ReportCardsComponent implements OnDestroy {
  @ViewChild(ReportCourseViewComponent)
  courseViewComponent!: ReportCourseViewComponent;

  private reportService = inject(ReportService);
  private authService = inject(AuthService);
  private lookupService = inject(LookupService);
  private fb = inject(FormBuilder);
  private destroy$ = new Subject<void>();

  user = this.authService.getCurrentUser();
  districtId = null;

  districtCardData: IReportCardGraph = {};
  completionsCardData: IReportCardGraph = {};
  usersCardData: IReportCardGraph = {};
  graphType = GraphType;
  currentDistrict: IDistrict | null = null;
  searchForm!: FormGroup;

  isDistrictLoading: boolean = false;
  isCompletionsLoading: boolean = false;
  isUsersLoading: boolean = false;

  currentSchoolYearId: number | null = null;
  get schoolYearId() {
    return this.searchForm.get('schoolYearId');
  }

  ngOnInit(): void {
    this.initializeForm();

    const district$ = this.lookupService.districtSubject$
      .pipe(startWith<IDistrict | null>(null), distinctUntilChanged((a, b) => a?.id === b?.id));

    const year$ = this.lookupService.schoolYearSubject$
      .pipe(startWith<number | null>(null), distinctUntilChanged());

    combineLatest([district$, year$])
      .pipe(
        tap(() => {
        }),
        filter(([_, year]) => year !== null),
        distinctUntilChanged(([d1, y1], [d2, y2]) => d1?.id === d2?.id && y1 === y2),
        takeUntil(this.destroy$)
      )
      .subscribe(([district, year]) => {
        this.currentDistrict = district;
        this.currentSchoolYearId = year;
        this.genereateCardData(district?.id ?? null);
      });
  }

  initializeForm(currentYear?: ISchoolYear): void {
    this.searchForm = this.fb.group({
      schoolYearId: [null],
      districtId: [null]
    });
  }

  getCurrentSchoolYear(): void {
    this.lookupService.getCurrentSchoolYear().pipe(
      take(1),
      tap((res: ISchoolYear) => {
        this.schoolYearId?.setValue(res.id);
        this.lookupService.schoolYearSubject$.next(res.id);
      })
    ).subscribe();
  }

  getDistrictData(districtId?: number | null): void {
    this.isDistrictLoading = true;
    if (this.user?.roleId === Role.DistrictAdmin && districtId) {
      this.reportService.getSchoolCountWithoutGlobalLoading(districtId || 0).pipe(
        take(1)
      ).subscribe((res: IReportCardSchoolCount[]) => {
        this.districtCardData = {
          total: res[0]?.totalSchoolCount,
          labels: res.map((item: IReportCardSchoolCount) => item.monthName),
          data: res.map((item: IReportCardSchoolCount) => item.schoolCount),
          type: 'schools',
          title: 'Total Schools'
        };
        this.isDistrictLoading = false;
      });
    } else {
      this.reportService.getDistrictCountWithoutGlobalLoading().pipe(
        take(1)
      ).subscribe((res: IReportCardDistrictCount[]) => {
        this.districtCardData = {
          total: res[0].totalDistrictCount,
          labels: res.map((item: IReportCardDistrictCount) => item.monthName),
          data: res.map((item: IReportCardDistrictCount) => item.districtCount),
          type: 'districts',
          title: 'Total Districts'
        };
        this.isDistrictLoading = false;
      });
    }
  }

  getTotalCompletions(districtId: number | null, year: number | null): void {
    this.isCompletionsLoading = true;
    const payload: IReportCardRequest = {
      isCompleted: true,
      districtId,
      schoolYearId: year
    };

    this.reportService.getCardInfoWithoutGlobalLoading(payload)
      .pipe(take(1))
      .subscribe((res: IReportCard) => {
        this.completionsCardData = {
          total:  res.totalCompletedCourses,
          labels: ['Completion', 'Incomplete'],
          data:   [res.progressPercent, 100 - res.progressPercent],
          type:   'completions',
          title:  'Total Completions'
        };
        this.isCompletionsLoading = false;
      });
  }

  getInactiveUsers(districtId?: number | null): void {
    if (districtId) {
      this.reportService.getCardInactiveInfo(districtId || 0).pipe(
        take(1)
      )
      .subscribe((res: IReportCardInactiveCount) => {
        this.usersCardData = {
          total: res.inactiveCount,
          labels: ['Inactive Users', 'Incomplete Users'],
          data: [res.inactivePercentage, res.inProgressPercentage],
          type: 'users',
          title: 'TOTAL INACTIVE USERS'
        };
      });
    }
  }

  genereateCardData(districtId: number | null): void {
    this.getDistrictData(districtId);
    this.getTotalCompletions(districtId, this.currentSchoolYearId);
    this.getInactiveUsers(districtId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
